import { isNilOrEmpty } from 'ramda-extension';
import { createAction } from 'redux-actions';

import { replaceData, resetForm } from '@gef-ui/form-core/stream/transfer/control';

import { appendMode } from 'constants/general';
import fetch from 'features/common/fetch';
import { pushToFilterDataLayer } from 'features/common/gtm/utils';
import user from 'features/views/user';
import { trimFormData } from 'utils';

import * as types from './actionTypes';
import { formDataFieldsMap } from './constants';
import { CAMPAIGNS_LIST_FORM_NAME } from './form';
import { getFullSort } from './selectors';
import { queryParamsFactory } from './utils';

const appendCampaigns = createAction(types.APPEND_CAMPAIGNS, (campaigns) => ({ campaigns }));
const reset = createAction(types.RESET);
const setCampaigns = createAction(types.SET_CAMPAIGNS, (campaigns) => ({ campaigns }));
const setSort = createAction(types.SET_SORT, (sort) => ({ sort }));

const fetchData =
	({ queryParams, callGtmFilter }, append) =>
	async (dispatch, getState, { getFormData }) => {
		let fetchUrl;
		const isCampaignSettings = window.location.pathname === '/campaign/settings/list';
		let filterSection;

		if (isCampaignSettings) {
			fetchUrl = fetch.constants.urls.campaignListSettings;
			filterSection = 'Nastavení kampaní - seznam kampaní';
		} else {
			fetchUrl = fetch.constants.urls.campaignList;
			filterSection = 'Řízení kampaní - seznam kampaní';
		}

		const formData = await getFormData(CAMPAIGNS_LIST_FORM_NAME);

		dispatch(
			fetch.actions.get(fetchUrl, {
				takeLatest: true,
				queryParams,
				onSuccess: [
					(response) => () => {
						const data = response.body.data;
						if (!data || !Array.isArray(data)) return;

						// called only on basic search/filter, not on sort/loadMore/loadPage etc.
						if (callGtmFilter) {
							pushToFilterDataLayer({
								filterSection,
								formData,
								formDataFieldsMap,
							});
						}
						dispatch(append === appendMode.APPEND ? appendCampaigns(data) : setCampaigns(data));
					},
				],
			}),
		);
	};

export const reload = (url) => (dispatch, getState) => {
	const { queryParams } = fetch.selectors.getLastRequest(url)(getState());
	dispatch(fetchData({ queryParams }, appendMode.SET));
};

export const loadSorted = (sort, url) => (dispatch, getState) => {
	const { queryParams } = fetch.selectors.getLastRequest(url)(getState());
	const newQueryParams = { ...queryParams, sort };
	dispatch(fetchData({ queryParams: newQueryParams }, appendMode.SET));
};

export const loadMore = (url) => (dispatch, getState) => {
	const { queryParams } = fetch.selectors.getLastRequest(url)(getState());
	const newQueryParams = { ...queryParams, page: queryParams.page + 1 };
	dispatch(fetchData({ queryParams: newQueryParams }, appendMode.APPEND));
};

export const loadPage = (page, url) => (dispatch, getState) => {
	const { queryParams } = fetch.selectors.getLastRequest(url)(getState());
	const newQueryParams = { ...queryParams, page };
	dispatch(fetchData({ queryParams: newQueryParams }, appendMode.SET));
};

export const sortRecords = (sort, url) => (dispatch) => {
	// This is hack, adding id sort because database is otherwise removing records.
	const sortWithId = [sort, 'id,desc'];
	dispatch(setSort(sortWithId));
	dispatch(loadSorted(sortWithId, url));
};

export const deleteCampaign = (campaignId) => (dispatch) => {
	dispatch(
		fetch.actions.deleteRequest(fetch.constants.urls.campaignDetail, {
			replaceParams: { id: campaignId },
			onSuccess: [
				() => () => {
					dispatch(startupCampaignsList());
				},
			],
		}),
	);
};

export const submitForm =
	() =>
	async (dispatch, getState, { getFormData }) => {
		const state = getState();
		const role = user.selectors.getCurrentRole(state);
		const data = await getFormData(CAMPAIGNS_LIST_FORM_NAME);
		const formData = trimFormData(data);
		const queryParams = queryParamsFactory(formData, role, getFullSort(state));

		dispatch(fetchData({ queryParams, callGtmFilter: true }, appendMode.SET));
	};

export const resetCriteria =
	() =>
	(dispatch, getState, { sendControlNext }) => {
		dispatch(reset());
		dispatch(fetch.actions.clearFetchStore(fetch.constants.urls.campaignListSettings));
		dispatch(fetch.actions.clearFetchStore(fetch.constants.urls.campaignList));
		sendControlNext(CAMPAIGNS_LIST_FORM_NAME, resetForm());
		dispatch(startupCampaignsList());
	};

export const startupCampaignsList =
	() =>
	(dispatch, getState, { sendControlNext }) => {
		const isCampaignSettings = window.location.pathname === '/campaign/settings/list';
		let url;
		if (isCampaignSettings) {
			url = fetch.constants.urls.campaignListSettings;
		} else {
			url = fetch.constants.urls.campaignList;
		}

		const lastRequest = fetch.selectors.getLastRequest(url)(getState());

		if (!isNilOrEmpty(lastRequest)) {
			const { campaignName, campaignStateId, channelIds } = lastRequest.queryParams;
			sendControlNext(
				CAMPAIGNS_LIST_FORM_NAME,
				replaceData({
					data: {
						campaignName,
						campaignState: campaignStateId,
						channel: channelIds,
					},
				}),
			);
			dispatch(reload(url));
		} else {
			dispatch(submitForm());
		}
	};
